import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import emotionStyled from '@emotion/styled'
import { IconButton, Slide } from '@material-ui/core'
import { Close } from '@material-ui/icons'
import useOutsideClick from '../../hooks/useOutsideClick'
import { css } from '@emotion/react'

/* ================= MODAL STYLES ================= */
const StyledModal = emotionStyled.div`
  position: fixed;
  inset: 0;
  z-index: 101;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: rgba(38,38,38,0.7);
  ${({ withBlur }) =>
    withBlur !== false &&
    `
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  `}
`

export const StyledModalWrapper = emotionStyled.div`
  position: relative;
  width: 450px;
  max-width: 95%;
  max-height: 95svh; 
  margin: 1.5rem auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100% !important;
      height: 100% !important;
    `}

  ${({ fitContent }) =>
    fitContent &&
    css`
      width: fit-content !important;
      max-height: 95svh;
      overflow: hidden;
    `}
`

const StyledModalRelative = emotionStyled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #fff;
  border-radius: 8px;
`

export const ModalBody = emotionStyled.div`
  flex: 1 1 auto;
  overflow: auto;
  padding: 12px;
  min-height: 150px;
  max-height: 100%;

  &::-webkit-scrollbar {
    background: transparent;
    width: 0.3rem;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(21,21,21,0.3);
    border-radius: 1000px;
  }
`

const StyledModalHeader = emotionStyled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #eee;
  box-shadow: 0 4px 2px -2px #ededed;
`

ModalBody.displayName = 'ModalBody'

export const ModalFooter = emotionStyled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  padding: 12px;
`

export const ModalHeader = ({ text, handleClose, children }) => {
  return (
    <StyledModalHeader>
      <div
        style={{
          fontWeight: 600,
          fontSize: 16,
          lineHeight: '24px',
          color: '#202939',
        }}
      >
        {text || children}
      </div>
      <IconButton onClick={handleClose} size='small'>
        <Close fontSize='inherit' />
      </IconButton>
    </StyledModalHeader>
  )
}
ModalHeader.propTypes = {
  text: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  children: PropTypes.node,
}

/* ================= MODAL COMPONENT ================= */
const Modal = ({
  open = false,
  onClose,
  disableOutsideClick = false,
  disabled,
  render = () => <></>,
  header = {},
  fullWidth = false,
  fitContent = false,
  withBlur = false,
}) => {
  const [showModal, setShowModal] = useState(open)
  const modalRef = useRef()

  useEffect(() => {
    setShowModal(open)
  }, [open])

  const handleClose = useCallback(() => {
    if (disabled) return
    setShowModal(false)
    setTimeout(onClose, 300)
  }, [disabled, onClose])

  useOutsideClick(modalRef, !disableOutsideClick ? handleClose : null)

  // disable body scroll when modal open
  useEffect(() => {
    const bodyEl = document.body
    if (showModal) bodyEl.classList.add('body--no-scroll')
    else bodyEl.classList.remove('body--no-scroll')
    return () => bodyEl.classList.remove('body--no-scroll')
  }, [showModal])

  if (!showModal) return null

  const content = render({ fullWidth, fitContent })

  const wrappedContent =
    React.isValidElement(content) &&
    content.type?.displayName === 'ModalBody' ? (
      content
    ) : (
      <ModalBody fullWidth={fullWidth} fitContent={fitContent}>
        {content}
      </ModalBody>
    )

  return (
    <StyledModal withBlur={withBlur}>
      <StyledModalWrapper fullWidth={fullWidth} fitContent={fitContent}>
        <Slide direction='up' in={showModal} mountOnEnter unmountOnExit>
          <StyledModalRelative ref={modalRef}>
            <ModalHeader {...header} handleClose={handleClose} />
            {wrappedContent}
          </StyledModalRelative>
        </Slide>
      </StyledModalWrapper>
    </StyledModal>
  )
}

Modal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  disableOutsideClick: PropTypes.bool,
  disabled: PropTypes.bool,
  render: PropTypes.func,
  header: PropTypes.shape({
    text: PropTypes.string,
    children: PropTypes.node,
  }),
  fullWidth: PropTypes.bool,
  fitContent: PropTypes.bool,
}

export default Modal
